import { useState } from "react";
import { Button } from "@/components/_components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/_components/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import { cn } from "@/lib/utils";
import { textareaStyles } from "theme/components/textarea";
import toast from "react-hot-toast";
import * as api from "@/lib/api/client";
import type { ConstantMessageDto } from "@/lib/api/types";
import { useErrorHandler } from "@/hooks/useErrorHandler";

interface UpdateConstantMessageDialogProps {
  constant: ConstantMessageDto;
  onSuccess: () => void;
  trigger?: React.ReactNode;
  mode?: "edit" | "create";
}

const CHANNEL_OPTIONS = [
  { value: "IM", label: "IM" },
  { value: "Serviceo App", label: "Serviceo App" },
  { value: "Voice", label: "Voice" },
];

const UpdateConstantMessageDialog: React.FC<UpdateConstantMessageDialogProps> = ({
  constant,
  onSuccess,
  trigger,
  mode = "edit",
}) => {
  const { handleError } = useErrorHandler();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    channel: constant.channel || "",
    messageValue: constant.messageValue || "",
    messageDesc: constant.messageDesc || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === "create" && !formData.channel) {
      toast.error("Channel is required");
      return;
    }

    if (!formData.messageValue.trim()) {
      toast.error("Message Value is required");
      return;
    }

    try {
      setIsLoading(true);
      
      if (mode === "create") {
        await api.createConstantMessage({
          agentType: constant.agentType,
          messageKey: constant.messageKey,
          language: constant.language,
          channel: formData.channel,
          messageValue: formData.messageValue.trim(),
          messageDesc: formData.messageDesc.trim() || undefined,
        });
        toast.success("Constant message added successfully");
      } else {
        await api.updateConstantMessage({
          id: constant.id,
          messageValue: formData.messageValue.trim(),
          messageDesc: formData.messageDesc.trim() || undefined,
        });
        toast.success("Constant message updated successfully");
      }
      
      setOpen(false);
      onSuccess();
    } catch (error: unknown) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setFormData({
        channel: constant.channel || "",
        messageValue: constant.messageValue || "",
        messageDesc: constant.messageDesc || "",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Add Constant Message" : "Update Constant Message"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create" 
                ? "Add a new constant message for a different channel."
                : "Update the message value and description for this constant."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="agentType" className="text-muted-foreground">
                Agent Type
              </Label>
              <Input
                id="agentType"
                value={constant.agentType}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="messageKey" className="text-muted-foreground">
                Message Key
              </Label>
              <Input
                id="messageKey"
                value={constant.messageKey}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="language" className="text-muted-foreground">
                Language
              </Label>
              <Input
                id="language"
                value={constant.language}
                disabled
                className="bg-muted"
              />
            </div>

            {mode === "create" && (
              <div className="grid gap-2">
                <Label htmlFor="channel">
                  Channel <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.channel}
                  onValueChange={(value) => setFormData({ ...formData, channel: value })}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select channel" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANNEL_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="messageValue">
                Message Value <span className="text-destructive">*</span>
              </Label>
              <textarea
                id="messageValue"
                value={formData.messageValue}
                onChange={(e) => setFormData({ ...formData, messageValue: e.target.value })}
                placeholder="Enter message value"
                required
                disabled={isLoading}
                className={cn(textareaStyles, "min-h-[100px]")}
                rows={4}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="messageDesc">Message Description</Label>
              <textarea
                id="messageDesc"
                value={formData.messageDesc}
                onChange={(e) => setFormData({ ...formData, messageDesc: e.target.value })}
                placeholder="Enter message description (optional)"
                disabled={isLoading}
                className={cn(textareaStyles, "min-h-[80px]")}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading 
                ? (mode === "create" ? "Adding..." : "Saving...") 
                : (mode === "create" ? "Add" : "Save Changes")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateConstantMessageDialog;
